/**
 * 分镜 prompt 模板库
 *
 * 给用户在分镜 / 帧 / 视频指导提示词输入框旁边的"快速插入"按钮提供数据。
 * 模板内容是中性中文片段，按"景别 / 镜头运动 / 节奏 / 情绪 / 转场 / 光影"6 组分类。
 *
 * 注意：
 * - 只做"插入"语义（追加到光标位置），不做"替换"
 * - 不引入 LLM，不持久化（首期纯客户端常量）
 * - 后续若需要"用户自定义模板"再升级到 DB-driven
 */

import type { DirectorPromptTemplateGroup } from '../services/directorApi';

export interface PromptTemplate {
  /** UI 显示的简短名（按钮 label） */
  label: string;
  /** 插入到 prompt 中的完整片段 */
  text: string;
}

export interface PromptTemplateGroup {
  /** 折叠组标题 */
  groupLabel: string;
  /** 折叠面板里展开后的描述（可选） */
  groupHint?: string;
  templates: PromptTemplate[];
}

export const PROMPT_TEMPLATE_GROUPS: PromptTemplateGroup[] = [
  // ---------------- 1. 景别 ----------------
  {
    groupLabel: '景别',
    groupHint: '画面里主体占多大、与环境的关系',
    templates: [
      { label: '远景', text: '远景，主体在画面中占比小，强调环境氛围' },
      { label: '全景', text: '全景，能看到主体全身，交代动作与位置' },
      { label: '中景', text: '中景，从腰部以上，适合对话与情绪' },
      { label: '特写', text: '特写，聚焦面部表情，强调情感' },
      { label: '极特写', text: '极特写，眼睛 / 手指 / 物件局部，强烈情绪压迫' },
    ],
  },
  // ---------------- 2. 镜头运动 ----------------
  {
    groupLabel: '镜头',
    groupHint: '摄影机在镜头里如何运动',
    templates: [
      { label: '缓慢推镜', text: '镜头缓慢推进，从全景逐渐过渡到主体，营造代入感' },
      { label: '缓慢拉镜', text: '镜头缓慢拉远，从特写过渡到全景，揭示环境' },
      { label: '平移跟拍', text: '镜头水平跟随主体移动，保持主体在画面中央' },
      { label: '环绕镜头', text: '镜头围绕主体缓慢环绕，强调立体感' },
      { label: '手持晃动', text: '轻微手持晃动，营造纪实 / 紧张氛围' },
      { label: '固定镜头', text: '镜头固定，画面稳定，让主体动作主导节奏' },
      { label: '俯拍', text: '高角度俯拍，强化主体的渺小或孤立感' },
      { label: '仰拍', text: '低角度仰拍，强化主体的力量或威慑感' },
    ],
  },
  // ---------------- 3. 节奏 / 时间 ----------------
  {
    groupLabel: '节奏',
    groupHint: '时间在镜头内的流速',
    templates: [
      { label: '慢动作', text: '慢动作，时间被拉长，强调瞬间细节' },
      { label: '时间凝固', text: '时间凝固，画面中只有一个元素在运动，其他静止' },
      { label: '加速', text: '加速运动，节奏紧张，强调速度感' },
      { label: '正常节奏', text: '自然时间流动' },
      { label: '定格瞬间', text: '关键瞬间画面定格 0.3 秒，再恢复正常' },
    ],
  },
  // ---------------- 4. 情绪 / 氛围 ----------------
  {
    groupLabel: '情绪',
    groupHint: '色彩与质感传达的整体感受',
    templates: [
      { label: '温暖治愈', text: '暖色调，柔光，温馨治愈氛围' },
      { label: '紧张悬疑', text: '冷色调，明暗对比强烈，营造悬疑紧张' },
      { label: '梦幻唯美', text: '柔焦，光晕，梦幻唯美氛围' },
      { label: '写实粗粝', text: '高对比，颗粒感，纪录片式写实' },
      { label: '复古胶片', text: '低饱和，颗粒感，复古胶片质感' },
      { label: '赛博朋克', text: '霓虹冷光，强烈青紫对比，赛博朋克氛围' },
    ],
  },
  // ---------------- 5. 转场 ----------------
  {
    groupLabel: '转场',
    groupHint: '镜头与镜头之间的切换方式（写在镜头开头或结尾）',
    templates: [
      { label: '硬切', text: '硬切入画，无过渡' },
      { label: '淡入', text: '从黑场缓慢淡入，营造时间过渡' },
      { label: '淡出', text: '画面缓慢淡出至黑场，承接下一镜' },
      { label: '划入', text: '画面从一侧划入，强调空间转移' },
      { label: '匹配剪辑', text: '画面构图与上一镜形成形状 / 动作对应，平滑过渡' },
    ],
  },
  // ---------------- 6. 光影 ----------------
  {
    groupLabel: '光影',
    groupHint: '光线方向与质感',
    templates: [
      { label: '自然光', text: '柔和自然光从窗外洒入，真实通透' },
      { label: '逆光', text: '强烈逆光，主体边缘被光勾出剪影' },
      { label: '侧光', text: '侧面打光，强化立体轮廓与质感' },
      { label: '硬光', text: '硬质聚光，明暗界限锐利，戏剧感强' },
      { label: '黄昏暖光', text: '黄昏低角度暖光，金色斜射' },
      { label: '夜景冷光', text: '夜晚冷色调，城市霓虹与街灯混合光源' },
    ],
  },
];

export function normalizePromptTemplateGroups(
  groups: DirectorPromptTemplateGroup[] | null | undefined
): PromptTemplateGroup[] {
  if (!Array.isArray(groups)) return PROMPT_TEMPLATE_GROUPS;

  const normalized = groups
    .map((group) => {
      const groupLabel = typeof group.groupLabel === 'string' ? group.groupLabel.trim() : '';
      const groupHint =
        typeof group.groupHint === 'string' && group.groupHint.trim().length > 0
          ? group.groupHint.trim()
          : undefined;
      const templates = Array.isArray(group.templates)
        ? group.templates
            .map((template) => {
              const label = typeof template.label === 'string' ? template.label.trim() : '';
              const text = typeof template.text === 'string' ? template.text.trim() : '';
              if (!label || !text) return null;
              return { label, text };
            })
            .filter((template): template is PromptTemplate => !!template)
        : [];

      if (!groupLabel || templates.length === 0) return null;
      return {
        groupLabel,
        ...(groupHint ? { groupHint } : {}),
        templates,
      };
    })
    .filter((group): group is PromptTemplateGroup => !!group);

  return normalized.length > 0 ? normalized : PROMPT_TEMPLATE_GROUPS;
}
